import {
  type ColumnDef,
  createColumnHelper,
  DataTable,
  type PaginationState,
} from "@aurumiq/ui/composite/data-table";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { dmyTime } from "@aurumiq/ui/lib/format";
import { useState } from "react";
import { errorMessage } from "@/lib/api";
import type { BillingEvent } from "@/lib/types";
import { useBillingEvents } from "./queries";

const col = createColumnHelper<BillingEvent>();

const columns = [
  col.accessor("type", {
    header: "Event",
    cell: (c) => <span className="font-mono text-xs">{c.getValue()}</span>,
  }),
  col.accessor("id", {
    header: "ID",
    cell: (c) => <span className="font-mono text-muted-foreground text-xs">{c.getValue()}</span>,
  }),
  col.accessor("received_at", { header: "Received", cell: (c) => dmyTime(c.getValue()) }),
  col.accessor("processed_at", {
    header: "Processed",
    cell: (c) => (c.getValue() ? dmyTime(c.getValue()) : "—"),
  }),
  col.accessor("error", {
    header: "Result",
    cell: (c) =>
      c.getValue() ? (
        <span className="inline-flex items-center gap-1.5">
          <StatusTag variant="err">FAILED</StatusTag>
          <span className="text-danger text-xs">{c.getValue()}</span>
        </span>
      ) : c.row.original.processed_at ? (
        <StatusTag variant="ok">OK</StatusTag>
      ) : (
        <StatusTag variant="warn">PENDING</StatusTag>
      ),
  }),
] as ColumnDef<BillingEvent, unknown>[];

export function EventsTab() {
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  const events = useBillingEvents(pagination.pageIndex + 1);
  const total = events.data?.total ?? 0;
  return (
    <DataTable
      columns={columns}
      data={events.data?.items ?? []}
      isLoading={events.isPending}
      getRowId={(e) => e.id}
      pageCount={Math.max(1, Math.ceil(total / pagination.pageSize))}
      pagination={pagination}
      onPaginationChange={setPagination}
      emptyMessage={
        events.isError
          ? `Could not load webhook events: ${errorMessage(events.error)}`
          : "No webhook events received."
      }
    />
  );
}
